import React, { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { EmptyState, Skeleton } from "@/components/EmptyState";
import { useLocations } from "@/lib/locations";
import { Beaker, Plus, X, Trash2, Calendar, MapPin, Layers, Hash, FlaskConical, GitBranch } from "lucide-react";
import { LotPickerInline } from "@/components/LotPickerInline";
import { TankTraceabilitySheet } from "@/components/TankTraceabilitySheet";

const SIZES_FOR_TANK = ["", "100ml", "300ml", "1L", "5L", "10L", "20L"];

export default function Tanks() {
  const locCtx = useLocations() || {};
  const { activeId, locationParams } = locCtx;
  const [tanks, setTanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [traceFor, setTraceFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tanks", { params: locationParams });
      setTanks(data.tanks || []);
    } catch (e) { toast.error(extractErr(e)); }
    setLoading(false);
  }, [locationParams]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (t) => {
    try {
      const { data } = await api.delete(`/tanks/${t.id}`);
      toast.success(data.restored ? "Tank deleted — raw materials restored" : "Tank deleted");
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <AppShell
      title="Tanks / Batches"
      subtitle={`${tanks.length} batch${tanks.length === 1 ? "" : "es"} — fill a tank with raw materials and we'll deduct from inventory automatically${activeId !== "all" ? " · scoped to active location" : ""}`}
      headerActions={<Button size="sm" onClick={() => setOpen(true)} data-testid="tanks-create-button"><Plus className="w-4 h-4 mr-1.5" />Fill new tank</Button>}
    >
      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      ) : tanks.length === 0 ? (
        <EmptyState
          icon={Beaker}
          title="No tanks created yet"
          description="Create a tank batch to record which raw materials were combined and consumed for a production run. Quantities are deducted from inventory the moment you save."
          action={<Button onClick={() => setOpen(true)} data-testid="tanks-empty-create"><Plus className="w-4 h-4 mr-1.5" />Fill first tank</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {tanks.map((t) => (
            <div key={t.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`tank-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] font-mono text-muted-foreground">{t.batch_number}</div>
                  <div className="font-heading font-semibold text-base truncate">{t.name}</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(t.created_at).toLocaleString()}</span>
                    {t.size && <span className="inline-flex items-center gap-1"><FlaskConical className="w-3 h-3" />Target: {t.size}</span>}
                    {t.expected_volume_l != null && <span>{t.expected_volume_l} L</span>}
                    <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" />{(t.materials || []).length} materials</span>
                    {(t.linked_orders_count || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 text-primary font-medium" data-testid={`tank-card-${t.id}-linked-orders`}>
                        <GitBranch className="w-3 h-3" />
                        {t.linked_orders_count} order{t.linked_orders_count === 1 ? "" : "s"}
                      </span>
                    )}
                    <span className="font-mono">€{t.total_cost?.toFixed?.(2) || "0.00"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    onClick={() => setTraceFor(t.id)}
                    title="View traceability"
                    data-testid={`tank-trace-${t.id}`}
                  >
                    <GitBranch className="w-3.5 h-3.5 mr-1" />Trace
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`tank-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete tank batch?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {(t.linked_orders_count || 0) > 0
                            ? "This tank is linked to one or more production orders. Unlink them first."
                            : "All raw materials consumed by this batch will be restored to inventory."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => onDelete(t)}
                          disabled={(t.linked_orders_count || 0) > 0}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="mt-3 rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium p-2">Material</th>
                      <th className="text-right font-medium p-2">Qty</th>
                      <th className="text-left font-medium p-2">Lot #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(t.materials || []).map((m, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2">
                          <div className="font-medium truncate">{m.item_name}</div>
                          <div className="font-mono text-[10px] text-muted-foreground">{m.item_sku}</div>
                        </td>
                        <td className="p-2 text-right font-mono tabular-nums">{m.quantity} {m.unit || ""}</td>
                        <td className="p-2 text-xs font-mono">{m.lot_number || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {t.notes && <div className="mt-2 text-xs text-muted-foreground">{t.notes}</div>}
            </div>
          ))}
        </div>
      )}

      {open && <TankForm open={open} onClose={() => setOpen(false)} onCreated={load} />}
      {traceFor && <TankTraceabilitySheet tankId={traceFor} onClose={() => setTraceFor(null)} />}
    </AppShell>
  );
}

function TankForm({ open, onClose, onCreated }) {
  const locCtx = useLocations() || {};
  const { locations = [], activeId } = locCtx;
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    name: "", size: "", expected_volume_l: "",
    location_id: activeId !== "all" ? activeId : (locations.find((l) => l.is_default)?.id || ""),
    notes: "",
    materials: [{ item_id: "", quantity: 0, lot_number: "", notes: "", _tracked: true, _allocations: [] }],
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/inventory?category=Raw Materials&limit=2000").then(({ data }) => setItems(data.items || []))
      .catch(() => api.get("/inventory?limit=2000").then(({ data }) => setItems(data.items || [])));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateMat = (i, k, v) => setForm((f) => {
    const c = [...f.materials];
    c[i] = { ...c[i], [k]: v };
    // When the item changes, clear allocations (lots are item-scoped)
    if (k === "item_id") {
      c[i]._allocations = [];
    }
    return { ...f, materials: c };
  });
  const addMat = () => setForm((f) => ({ ...f, materials: [...f.materials, { item_id: "", quantity: 0, lot_number: "", notes: "", _tracked: true, _allocations: [] }] }));
  const removeMat = (i) => setForm((f) => ({ ...f, materials: f.materials.filter((_, idx) => idx !== i) }));

  const totalCost = form.materials.reduce((s, m) => {
    const it = items.find((i) => i.id === m.item_id);
    return s + (Number(m.quantity) || 0) * (Number(it?.unit_cost) || 0);
  }, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Tank name is required"); return; }
    const valid = form.materials.filter((m) => m.item_id && Number(m.quantity) > 0);
    if (valid.length === 0) { toast.error("Add at least one material with qty > 0"); return; }
    // Validate lot allocations on tracked lines
    for (const m of valid) {
      if (m._tracked && (m._allocations || []).length > 0) {
        const total = m._allocations.reduce((s, a) => s + (Number(a.quantity) || 0), 0);
        if (Math.abs(total - Number(m.quantity)) > 1e-6) {
          toast.error(`Lot allocations on tracked materials must sum to the line qty (${m.quantity})`);
          return;
        }
        if (m._allocations.some((a) => !a.lot_id || Number(a.quantity) <= 0)) {
          toast.error("Each lot allocation needs a lot and qty > 0");
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      await api.post("/tanks", {
        ...form,
        size: form.size || null,
        expected_volume_l: form.expected_volume_l ? Number(form.expected_volume_l) : null,
        location_id: form.location_id || null,
        materials: valid.map((m) => {
          const lots = (m._tracked && (m._allocations || []).length > 0)
            ? m._allocations.map((a) => ({
                lot_id: a.lot_id,
                lot_number: a.lot_number || null,
                quantity: Number(a.quantity),
              }))
            : null;
          return {
            item_id: m.item_id,
            quantity: Number(m.quantity),
            lot_number: m.lot_number || null,
            notes: m.notes || null,
            lots,
          };
        }),
      });
      toast.success("Tank batch created — inventory deducted");
      onClose(); onCreated();
    } catch (e) { toast.error(extractErr(e)); }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fill new tank / batch</DialogTitle>
          <DialogDescription>
            Pick raw materials & quantities. The moment you save, those quantities are deducted from inventory and a permanent batch record is kept (with timestamps and lot numbers).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2 space-y-1.5">
              <Label>Tank / batch name *</Label>
              <Input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Tank 1 — Calf Drink Base" data-testid="tank-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Target size (optional)</Label>
              <Select value={form.size || "none"} onValueChange={(v) => update("size", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="tank-form-size"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {SIZES_FOR_TANK.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Expected volume (L)</Label>
              <Input type="number" step="any" value={form.expected_volume_l} onChange={(e) => update("expected_volume_l", e.target.value)} placeholder="e.g. 500" data-testid="tank-form-volume" />
            </div>
            {locations.length > 1 && (
              <div className="md:col-span-2 space-y-1.5">
                <Label className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />Location</Label>
                <Select value={form.location_id || "auto"} onValueChange={(v) => update("location_id", v === "auto" ? "" : v)}>
                  <SelectTrigger data-testid="tank-form-location"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Default location</SelectItem>
                    {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="md:col-span-3 space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} placeholder="e.g. Mixed at 60°C, 40 min agitation…" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Raw materials *</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addMat} data-testid="tank-form-add-line"><Plus className="w-4 h-4 mr-1" />Add material</Button>
            </div>
            <div className="mt-2 space-y-2">
              {form.materials.map((m, i) => {
                const item = items.find((it) => it.id === m.item_id);
                return (
                  <div key={i} className="rounded-lg border border-border p-2 space-y-2">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-12 md:col-span-5 space-y-1">
                        <Label className="text-[11px]">Material (Raw)</Label>
                        <Select value={m.item_id} onValueChange={(v) => updateMat(i, "item_id", v)}>
                          <SelectTrigger data-testid={`tank-form-line-${i}-item`}><SelectValue placeholder="Pick from inventory…" /></SelectTrigger>
                          <SelectContent className="max-h-[280px]">
                            {items.map((it) => <SelectItem key={it.id} value={it.id}><span className="font-mono text-xs mr-2">{it.sku}</span>{it.name} · {it.quantity} {it.unit || ""}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-6 md:col-span-2 space-y-1">
                        <Label className="text-[11px]">Qty</Label>
                        <Input type="number" step="any" min="0" value={m.quantity} onChange={(e) => updateMat(i, "quantity", e.target.value)} data-testid={`tank-form-line-${i}-qty`} />
                        {item && <div className="text-[10px] text-muted-foreground">Avail: {item.quantity} {item.unit || ""}</div>}
                      </div>
                      <div className="col-span-6 md:col-span-2 space-y-1">
                        <Label className="text-[11px] flex items-center gap-1"><Hash className="w-3 h-3" />Legacy Lot #</Label>
                        <Input value={m.lot_number} onChange={(e) => updateMat(i, "lot_number", e.target.value)} placeholder="Optional" data-testid={`tank-form-line-${i}-lot`} />
                      </div>
                      <div className="col-span-10 md:col-span-2 space-y-1">
                        <Label className="text-[11px]">Notes</Label>
                        <Input value={m.notes} onChange={(e) => updateMat(i, "notes", e.target.value)} placeholder="Optional" />
                      </div>
                      <div className="col-span-2 md:col-span-1 flex justify-end">
                        {form.materials.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => removeMat(i)}><X className="w-4 h-4" /></Button>}
                      </div>
                    </div>
                    <LotPickerInline
                      itemId={m.item_id}
                      quantity={m.quantity}
                      allocations={m._allocations}
                      onChange={(allocs) => updateMat(i, "_allocations", allocs)}
                      tracked={m._tracked}
                      onTrackedChange={(b) => updateMat(i, "_tracked", b)}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2">
            <div className="text-sm">Estimated raw material cost</div>
            <div className="font-mono font-semibold">€{totalCost.toFixed(2)}</div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="tank-form-submit"><Beaker className="w-4 h-4 mr-1.5" />{submitting ? "Filling tank…" : "Fill tank & deduct"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
